'use client';

import {
  ButtonComponent,
  ButtonPriority,
  ButtonSize,
} from '@vaad1st/component.atom.button';
import { IconTravelMapMarkerComponent } from '@vaad1st/component.atom.icons';
import { TextFieldComponent } from '@vaad1st/component.atom.text-field';
import {
  TypographyComponent,
  TypographySize,
  TypographyVariant,
  TypographyWeight,
} from '@vaad1st/component.atom.typography';
import React, { ChangeEventHandler, useCallback, useState } from 'react';
import styles from './store-finder.component.module.less';

export interface StoreFinderComponentProps {
  title?: string;
  placeholderText?: string;
  buttonText?: string;
  currentLocationText?: string;
  appointmentSearchPath?: string;
  componentId?: string;
}

const APPOINTMENT_BOOKER_PATH =
  'https://retail.virginholidays.co.uk/retail-appointments/Search/';

const GEOLOCATION_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 10_000,
  maximumAge: 0,
};

function toPlusEncoding(input: string): string {
  return input.trim().replace(/\s+/g, '+');
}

function buildAppointmentUrl(
  base: string,
  queryParam: string,
  extraParams?: Record<string, string | number | boolean>,
) {
  const url = new URL(base);
  url.searchParams.set('q', queryParam);
  if (extraParams) {
    for (const [key, val] of Object.entries(extraParams)) {
      url.searchParams.set(key, String(val));
    }
  }
  return url.toString();
}

export default function StoreFinderComponent({
  title = 'Find a Store',
  placeholderText = 'Post code, city or town',
  buttonText = 'Find a store',
  currentLocationText = 'Use my current location',
  appointmentSearchPath = APPOINTMENT_BOOKER_PATH,
  componentId,
}: StoreFinderComponentProps) {
  const [inputValue, setInputValue] = useState('');
  const trimmed = inputValue.trim();

  const handleSubmitText = useCallback(() => {
    if (!trimmed) return;
    const queryParam = toPlusEncoding(trimmed);
    const appointmentUrl = buildAppointmentUrl(
      appointmentSearchPath,
      queryParam,
    );
    window.open(appointmentUrl, '_blank');
  }, [appointmentSearchPath, trimmed]);

  const handleSubmitGeoLocation = useCallback(() => {
    if (!navigator.geolocation) {
      return;
    }

    navigator.geolocation.getCurrentPosition(
      pos => {
        const { latitude, longitude } = pos.coords;
        const queryParam = `${latitude},${longitude}`;
        const appointmentUrl = buildAppointmentUrl(
          appointmentSearchPath,
          queryParam,
          {
            findme: 1,
          },
        );
        window.open(appointmentUrl, '_blank');
      },
      err => {
        console.error('Geolocation error', err);
      },
      GEOLOCATION_OPTIONS,
    );
  }, [appointmentSearchPath]);

  const handleChange: ChangeEventHandler<HTMLInputElement> = event => {
    setInputValue(event.currentTarget.value);
  };

  return (
    <div id={componentId} className={styles['store-finder']}>
      <TypographyComponent
        variant={TypographyVariant.Body}
        size={TypographySize.Sml}
        weight={TypographyWeight.Bold}>
        {title}
      </TypographyComponent>

      <div className={styles['store-finder__form']}>
        <TextFieldComponent
          className={styles['store-finder__form__text-field']}
          label=""
          onChange={handleChange}
          placeholder={placeholderText}
          value={inputValue}
        />

        <ButtonComponent
          as="button"
          id="store-finder-submit"
          text={buttonText}
          onClick={handleSubmitText}
        />
      </div>

      <div className={styles['store-finder__current-location']}>
        <IconTravelMapMarkerComponent aria-hidden />
        <ButtonComponent
          as="button"
          id="store-finder-geo"
          className={styles['store-finder__current-location__button']}
          text={currentLocationText}
          priority={ButtonPriority.Link}
          size={ButtonSize.Small}
          onClick={handleSubmitGeoLocation}
        />
      </div>
    </div>
  );
}
